import useAuthAPIStore from 'apis/auth/auth.api'
import { DEFAULT_ACCESS_ROLE, ROLES_KEY } from 'consts/auth/const'
import useAccessStore from 'stores/useAccessStore'
import useErrorStore from 'stores/useErrorStore'
import { AccessRoleType, ResponseRolesType } from 'types/auth/role/RoleType'

type RoleKey = keyof typeof ROLES_KEY

const useUpdateAccessRole = () => {
  const { getRoleCollections } = useAuthAPIStore()
  const { setAccessRole } = useAccessStore()
  const { setErrorCode } = useErrorStore()

  /**
   * 역할 문자열을 유효한 키로 변환
   * @param role - 변환할 역할 문자열
   * @returns 변환된 역할의 키 또는 유효하지 않은 경우 null
   */
  const convertRoleToKey = (role: string): RoleKey | null => {
    // 예: ROLE_GROUPS_CREATE/UPDATE/DELETE_GROUPS => ROLE_GROUPS_CREATE_UPDATE_DELETE_GROUPS
    const converted = role.replaceAll('/', '_') as RoleKey
    return ROLES_KEY[converted] ? converted : null
  }

  /**
   * 단일 역할에 대한 접근 권한 업데이트
   * @param currentRole - 현재 권한 상태
   * @param roleKey - 업데이트할 역할 키
   */
  const updateRoleAccess = (currentRole: AccessRoleType, roleKey: RoleKey): AccessRoleType => {
    // 예: { category: "groups", feature: "createUpdateDeleteGroups" }
    const { category, feature } = ROLES_KEY[roleKey]

    return {
      ...currentRole,
      // 키가 존재시 권한이 있는 것이므로 true로 설정
      [category]: { ...currentRole[category], [feature]: true },
    }
  }

  /**
   * 사용자 권한 정보 업데이트 메인 함수
   * @returns 업데이트 성공 여부
   */
  const updateAccessRole = async (): Promise<boolean> => {
    try {
      let response = await getRoleCollections()

      if (!response) {
        throw new Error('사용자 정보를 가져오는데 실패했습니다.')
      }

      // 전체 역할 목록 처리 및 권한 상태 업데이트
      const roles = response.roleCollections || []

      const updatedRole = roles.reduce(
        (acc: AccessRoleType, role: string) => {
          const roleKey = convertRoleToKey(role)
          if (!roleKey) return acc

          return updateRoleAccess(acc, roleKey)
        },
        { ...DEFAULT_ACCESS_ROLE }
      )

      const finalRole = applyFullAccess(updatedRole)
      setAccessRole(finalRole)
      return true
    } catch (error) {
      console.error('[updateAccessRole] 오류 발생:', error)
      setErrorCode(500)
      return false
    }
  }

  return { updateAccessRole }
}

/**
 * 카테고리별 fullAccess 처리
 * fullAccess가 true인 경우 해당 카테고리의 모든 권한을 활성화
 * @param role - 처리할 권한 객체
 */
const applyFullAccess = (role: AccessRoleType): AccessRoleType => {
  const updatedRole = { ...role }

  Object.entries(updatedRole).forEach(([key, value]) => {
    // 유효성 검사: value가 존재하고 객체이며 fullAccess 속성을 가지고 있는지 확인
    if (!value || typeof value !== 'object' || !('fullAccess' in value)) return

    // fullAccess가 true인 경우
    if (value.fullAccess) {
      // // 해당 카테고리의 모든 속성을 true로 설정
      Object.keys(value).forEach((subKey) => {
        value[subKey as keyof typeof value] = true
      })
    }
  })

  return updatedRole
}

export default useUpdateAccessRole
